import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Motion";
import { submitContact } from "@/lib/api";

const interests = ["General inquiry", "Refer a family", "Volunteer", "Partnership", "Donate"];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "General inquiry", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message || "Thank you for reaching out.");
      setForm({ name: "", email: "", phone: "", interest: "General inquiry", message: "" });
    } catch (err) {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#1A1A1A]/20 py-3 text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C85A3C] transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 px-6 md:px-10">
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C85A3C] mb-6">Get Involved</p>
            <h2 className="font-serif-display font-light leading-[1.0] tracking-tight text-4xl md:text-5xl text-[#1A1A1A]">
              Reach out, refer a family, or partner with us.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#1A1A1A]/70 max-w-md">
              Whether you're a caseworker, a family in need, or a community partner — we'd
              love to hear from you.
            </p>
            <div className="mt-10 space-y-3 text-sm text-[#1A1A1A]/70">
              <p data-testid="contact-email">info@independenceway.org</p>
              <p data-testid="contact-phone">(973) 618-8224</p>
              <p data-testid="contact-address">New Jersey, United States</p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <input data-testid="contact-name" className={inputCls} placeholder="Full name" value={form.name} onChange={update("name")} />
                <input data-testid="contact-email-input" type="email" className={inputCls} placeholder="Email address" value={form.email} onChange={update("email")} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <input data-testid="contact-phone-input" className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} />
                <select data-testid="contact-interest" className={`${inputCls} appearance-none cursor-pointer`} value={form.interest} onChange={update("interest")}>
                  {interests.map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>
              <textarea data-testid="contact-message" rows={4} className={`${inputCls} resize-none`} placeholder="How can we help?" value={form.message} onChange={update("message")} />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F4F1EA] rounded-full px-8 py-4 text-sm uppercase tracking-[0.15em] hover:bg-[#C85A3C] transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send Message <Send size={15} /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
